"use client";

import { useEffect, useState } from "react";
import { WalletProvider } from "@solana/wallet-adapter-react";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import {
  detectMetaMaskInstalled,
  initMetaMaskSolanaClient,
} from "@/config/metamaskSolana";
import { isPerfSensitiveMarketingPath } from "@/lib/perfMarketingPaths";

/**
 * Keeps WalletProvider mounted (header/hooks need it) but defers adapter
 * construction + MetaMask init until idle / first interaction so Lighthouse
 * and first paint are not blocked by wallet SDK work.
 */
export default function WalletShell({ children }) {
  const [wallets, setWallets] = useState([]);
  const [autoConnect, setAutoConnect] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let enabled = false;

    const enable = () => {
      if (cancelled || enabled) return;
      enabled = true;

      void import("@solana/wallet-adapter-react-ui/styles.css");
      void import("@/config/solana").then((mod) => {
        if (!cancelled) {
          setWallets(mod.walletAdapters);
          setAutoConnect(true);
        }
      });

      detectMetaMaskInstalled().then((installed) => {
        if (!cancelled && installed) {
          initMetaMaskSolanaClient();
        }
      });
    };

    const opts = { once: true, passive: true };
    document.addEventListener("pointerdown", enable, opts);
    document.addEventListener("keydown", enable, opts);
    document.addEventListener("touchstart", enable, opts);

    let idleId;
    let timeoutId;
    // Marketing landings: interaction-only (LH mobile scrolls + waits would
    // otherwise pull wallet SDKs into TBT). App routes: ~3.5s idle fallback.
    const marketing = isPerfSensitiveMarketingPath(window.location.pathname);
    if (!marketing) {
      const fallbackMs = 3500;
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(enable, { timeout: fallbackMs });
      }
      timeoutId = window.setTimeout(enable, fallbackMs);
    }

    return () => {
      cancelled = true;
      document.removeEventListener("pointerdown", enable);
      document.removeEventListener("keydown", enable);
      document.removeEventListener("touchstart", enable);
      if (idleId != null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <WalletProvider wallets={wallets} autoConnect={autoConnect}>
      <WalletModalProvider>{children}</WalletModalProvider>
    </WalletProvider>
  );
}
